import React from 'react';
import { Link } from 'react-router-dom';
import LegalLayout, { Clause, Fact, Email } from './LegalLayout';
import { LEGAL } from './legalDetails';

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy">

      <div className="lg-callout">
        <p className="lg-callout-title">Summary</p>
        <ul>
          <li>Pulso helps organisations receive reports, grievances and feedback from workers and stakeholders by WhatsApp, SMS, email and web form.</li>
          <li>When you send a message, we store it, work out what it's about, and pass it to the organisation you were contacting.</li>
          <li>We don't sell your information, and we don't use it for advertising. Only you can start the conversation with the organisation; they can't initiate it.</li>
          <li>You can ask to see, correct or delete your information at any time. See <Link to="/data-deletion">how to delete your data</Link>.</li>
        </ul>
      </div>

      <Clause n={1}>Who we are</Clause>
      <p>
        Pulso is operated by <strong>{LEGAL.companyName}</strong>, a South African company
        (registration number <Fact value={LEGAL.regNumber} />), with its address at <Fact value={LEGAL.address} />.
        In this policy "we", "us" and "our" mean {LEGAL.companyName}.
      </p>
      <p>
        This policy explains how we handle personal information through Pulso, and your rights under
        South Africa's Protection of Personal Information Act, 2013 (POPIA).
      </p>

      <Clause n={2}>Our role: we act for the organisation you contacted</Clause>
      <p>
        Pulso is used by organisations, such as mining companies and community-facing organisations
        (our "clients"), to receive and manage messages from the people they work with and alongside.
      </p>
      <p>
        When you send a message to one of our clients through Pulso, <strong>that client decides why and
        how your information is used</strong>. Under POPIA, they are the "responsible party". We are
        their "operator": we process your information on their behalf and on their instructions.
        If you have questions about what a client does with your report, you can also contact them directly.
      </p>
      <p>
        We act as the responsible party ourselves for a small amount of information, such as the details
        of people who sign up for or use the Pulso dashboard, and people who contact us or ask for a demo
        through this website.
      </p>

      <Clause n={3}>What we collect</Clause>
      <p>Depending on how you contact an organisation through Pulso, we may collect:</p>
      <ul>
        <li><strong>Phone numbers</strong>, if you send a WhatsApp message or SMS.</li>
        <li><strong>Your WhatsApp profile name</strong>, as WhatsApp shares it with the business you message.</li>
        <li><strong>Message content</strong>: the text you send, and translations of it, as well as any reply from the client organisation.</li>
        <li><strong>Media attachments</strong>, such as photos, documents and voice notes, plus text transcriptions of voice notes.</li>
        <li><strong>Email addresses</strong>, and the name you sign with, if you email or use a web form.</li>
        <li><strong>Site or location details</strong> you choose to include, for example which mine, site or area you're referencing.</li>
        <li><strong>Technical details</strong> that come with a message, such as the date and time it was sent and which channel it came through.</li>
      </ul>
      <p>
        Some web forms let you report anonymously. If you do, we don't ask for your name or contact details.
        Please keep in mind that anything you write in the message itself will still be stored.
      </p>
      <p>
        For people who use the Pulso dashboard, we also collect your name, work email address, organisation
        and login details. Passwords are stored in a scrambled (hashed) form, not as plain text.
      </p>

      <Clause n={4}>Why we collect it</Clause>
      <p>We use this information to:</p>
      <ul>
        <li><strong>Receive</strong> your report, grievance or feedback.</li>
        <li><strong>Classify</strong> it: translate it where needed, and identify its themes (for example safety, water, or employment) and its sentiment (positive, neutral or negative). Part of this is done automatically by an AI service (see <a href="#section-5">section 5</a>).</li>
        <li><strong>Route</strong> it to the right client organisation and, where relevant, the right site.</li>
        <li><strong>Manage</strong> it: show it in the client's dashboard, and let them track it and reply to you.</li>
        <li>Keep Pulso secure and working properly, and meet our legal obligations.</li>
      </ul>
      <p>
        We don't sell personal information, use it for advertising, or use your messages to train AI models.
      </p>
      <p>
        Automated classification helps people sort and prioritise messages. It isn't used to make decisions
        about you on its own. The client organisation's staff or representatives read and act on reports.
      </p>

      <Clause n={5}>Who we share it with</Clause>
      <p>We only share your information with:</p>
      <ul>
        <li><strong>The client organisation you contacted.</strong></li>
        <li>
          <strong>The service providers that make Pulso work.</strong> They process information only to provide
          their service to us. They are listed in the table below.
        </li>
        <li><strong>Authorities</strong>, where the law requires it, or where it's needed to protect someone's life or safety.</li>
      </ul>
      <div className="lg-table-wrap">
        <table className="lg-table">
          <caption>Service providers (operators) that process personal information for us</caption>
          <thead>
            <tr><th scope="col">Provider</th><th scope="col">What they do for Pulso</th></tr>
          </thead>
          <tbody>
            <tr><th scope="row">Twilio</th><td>Sends and receives WhatsApp messages, SMS and calls.</td></tr>
            <tr><th scope="row">Meta Platforms (WhatsApp)</th><td>Carries WhatsApp messages through the WhatsApp Business Platform.</td></tr>
            <tr><th scope="row">SendGrid <span className="lg-note">(a Twilio company)</span></th><td>Sends and receives email.</td></tr>
            <tr><th scope="row">Anthropic</th><td>Provides the AI used to classify messages by theme and sentiment and to detect their language.</td></tr>
            <tr><th scope="row">OpenAI</th><td>Transcribes voice notes into text.</td></tr>
            <tr><th scope="row">Google Cloud Translation</th><td>Translates messages.</td></tr>
            <tr><th scope="row">Vercel</th><td>Hosts the Pulso website and application.</td></tr>
            <tr><th scope="row"><Fact value={LEGAL.databaseProvider} /></th><td>Hosts our database.</td></tr>
          </tbody>
        </table>
      </div>
      <p>
        When you use WhatsApp, Meta's own privacy policy also applies to how WhatsApp handles your messages.
      </p>

      <Clause n={6}>Where your data is stored, and transfers outside South Africa</Clause>
      <p>
        Our database is hosted in <Fact value={LEGAL.dataStorageLocation} />. Several of the service providers
        listed above are based outside South Africa, mainly in the United States. That means your information
        may be transferred to and processed in other countries.
      </p>
      <p>
        We only make these transfers in ways POPIA allows (section 72). For example, the recipient must be bound
        by law or by a written agreement to protect personal information to a standard similar to POPIA's.
      </p>

      <Clause n={7}>How long we keep it</Clause>
      <p>
        We keep messages and related information for as long as the client organisation you contacted uses Pulso,
        unless they ask us to delete it sooner, you ask us to delete it (see <a href="#section-9">section 9</a>), or the law requires us
        to keep it longer.
      </p>
      <p>
        When a client organisation's agreement with us ends, we send them an export of their data, so they can
        still review past reports without Pulso. We then delete that data from Pulso after a <Fact value={LEGAL.postAgreementDeletionPeriod} /> period. From that point the client
        organisation holds its copy under its own privacy policy, so any questions about it should go to them.
      </p>
      <p>
        Copies of operational data in backups are cleared within <Fact value={LEGAL.backupRetentionPeriod} />,
        unless otherwise requested.
      </p>
      <p>
        Dashboard user accounts are kept for as long as the account is active, and deleted when the
        client organisation closes it.
      </p>

      <Clause n={8}>How we keep it safe</Clause>
      <p>We take reasonable technical and organisational steps to protect personal information, including:</p>
      <ul>
        <li>Encrypting information as it travels between you, us and our providers.</li>
        <li>Keeping each client organisation's data separate, so one client cannot see another's messages.</li>
        <li>Limiting dashboard access to authorised, logged-in users, and hashing passwords.</li>
        <li>Checking that incoming messages genuinely come from our messaging providers.</li>
        <li>Only working with providers who commit to protecting the information they process.</li>
      </ul>
      <p>
        No system is completely secure. If a security breach affects your personal information, we will tell
        the client organisation concerned and, where POPIA requires it, the Information Regulator and you.
      </p>

      <Clause n={9}>Your rights</Clause>
      <p>Under POPIA you have the right to:</p>
      <ul>
        <li><strong>Access</strong>: ask whether we hold information about you, and get a copy of it.</li>
        <li><strong>Correction</strong>: ask us to correct information that is wrong or out of date.</li>
        <li><strong>Deletion</strong>: ask us to delete your information. See <Link to="/data-deletion">how to delete your data</Link>.</li>
        <li><strong>Objection</strong>: object to us processing your information.</li>
        <li><strong>Complaint</strong>: complain to the Information Regulator (details below).</li>
      </ul>
      <p>
        To use any of these rights, email us at <Email value={LEGAL.contactEmail} />. Tell us the phone number or
        email address you used to contact the organisation, so we can find your information. We may need to confirm
        who you are before we act on a request. Because we act for our clients, we may involve the client organisation
        you contacted, and in some cases the law may allow or require us to keep certain information.
      </p>

      <Clause n={10}>Contact us and our Information Officer</Clause>
      <dl className="lg-contact">
        <div><dt>Company</dt><dd>{LEGAL.companyName}</dd></div>
        <div><dt>Registration number</dt><dd><Fact value={LEGAL.regNumber} /></dd></div>
        <div><dt>Address</dt><dd><Fact value={LEGAL.address} /></dd></div>
        <div><dt>Email</dt><dd><Email value={LEGAL.contactEmail} /></dd></div>
        <div>
          <dt>Information Officer</dt>
          <dd>
            {LEGAL.informationOfficerEmails.map((email, i) => (
              <React.Fragment key={email}>{i > 0 && ' or '}<Email value={email} /></React.Fragment>
            ))}
          </dd>
        </div>
      </dl>
      <p>
        If you're not happy with how we've handled your information, you can complain to the Information Regulator
        (South Africa) at <a href="https://inforegulator.org.za" target="_blank" rel="noreferrer">inforegulator.org.za</a>.
      </p>

      <Clause n={11}>Changes to this policy</Clause>
      <p>
        We may update this policy from time to time. When we do, we'll change the "last updated" date at the top of
        this page, and we'll let our client organisations know about significant changes.
      </p>

    </LegalLayout>
  );
}
